import { useState, useMemo, useEffect } from "react";
import { toast } from "sonner";
import { Sparkles, Music4, Loader2, Wand2, Coins, Lock, Plus, X, ArrowUp, ArrowDown, RefreshCw, Scissors, Link2, Languages, Headphones } from "lucide-react";
import api from "@/lib/api";
import { mediaUrl } from "@/lib/api";
import { songCredits, fmtCredits } from "@/lib/cost";
import { GENRES, VOICES } from "@/lib/options";
import { useDraft, clearDraft } from "@/lib/useDraft";
import { Chip } from "@/components/Chip";
import StatusBadge from "@/components/StatusBadge";
import WaveformPlayer from "@/components/WaveformPlayer";
import VoiceUploader from "@/components/VoiceUploader";


const SECTION_TYPES = [
  { id: "intro", label: "Intro" },
  { id: "verse", label: "Verso" },
  { id: "chorus", label: "Coro" },
  { id: "bridge", label: "Puente" },
  { id: "outro", label: "Outro" },
];

const STRUCTURE_TEMPLATES = [
  { name: "Pop", types: ["verse", "chorus", "verse", "chorus", "bridge", "chorus"] },
  { name: "Balada", types: ["intro", "verse", "chorus", "verse", "chorus", "outro"] },
  { name: "Urbano", types: ["intro", "chorus", "verse", "chorus", "bridge", "chorus"] },
];

const LANGUAGES = [{ id: "es", label: "Español" }, { id: "en", label: "Inglés" }, { id: "spanglish", label: "Spanglish" }];
const SLANGS = [{ id: "suave", label: "Suave" }, { id: "media", label: "Media" }, { id: "callejera", label: "Callejera" }];
const RHYMES = [{ id: "libre", label: "Libre" }, { id: "abab", label: "ABAB" }, { id: "aabb", label: "Pareados" }];

export default function SongPanel({ projects, reload }) {
  const [title, setTitle] = useDraft("mfp_draft_song_title", "");
  const [genre, setGenre] = useDraft("mfp_draft_song_genre", "Reguetón");
  const [voice, setVoice] = useDraft("mfp_draft_song_voice", "mine");
  const [mode, setMode] = useState("write"); // write | idea
  const [lyrics, setLyrics] = useDraft("mfp_draft_song_lyrics", "");
  const [idea, setIdea] = useDraft("mfp_draft_song_idea", "");
  const [duration, setDuration] = useDraft("mfp_draft_song_duration", 60);
  const [lockLyrics, setLockLyrics] = useDraft("mfp_draft_song_lock", false);
  const [language, setLanguage] = useDraft("mfp_draft_song_language", "es");
  const [slang, setSlang] = useDraft("mfp_draft_song_slang", "media");
  const [rhyme, setRhyme] = useDraft("mfp_draft_song_rhyme", "libre");
  const [voiceEs, setVoiceEs] = useDraft("mfp_draft_song_voice_es", "female");
  const [voiceEn, setVoiceEn] = useDraft("mfp_draft_song_voice_en", "male");
  const [regenTag, setRegenTag] = useState(null);
  const [translating, setTranslating] = useState(false);
  const [rerolling, setRerolling] = useState(false);
  const [annotating, setAnnotating] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewStatus, setPreviewStatus] = useState(null);
  const [previewAudio, setPreviewAudio] = useState(null);
  const [prons, setProns] = useDraft("mfp_draft_song_prons", []);
  const [sections, setSections] = useDraft("mfp_draft_song_sections", [{ type: "verse", text: "" }]);
  const [genLyrics, setGenLyrics] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lastId, setLastId] = useState(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("mfp_reedit");
      if (!raw) return;
      localStorage.removeItem("mfp_reedit");
      const d = JSON.parse(raw);
      if (d.title) setTitle(`${d.title} (nueva versión)`);
      if (d.genre) setGenre(d.genre);
      if (typeof d.lyrics === "string" && d.lyrics.trim()) { setLyrics(d.lyrics); setMode("write"); }
      if (d.lockLyrics != null) setLockLyrics(d.lockLyrics);
      toast.success("Editando una copia de tu canción. Ajusta la letra y vuelve a generar.");
    } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const current = projects.find((p) => p.id === lastId);
  const addPron = () => setProns([...prons, { word: "", sounds: "" }]);
  const updatePron = (i, k, v) => setProns(prons.map((p, idx) => idx === i ? { ...p, [k]: v } : p));
  const removePron = (i) => setProns(prons.filter((_, idx) => idx !== i));
  const addSection = () => setSections([...sections, { type: "verse", text: "" }]);
  const updateSection = (i, k, v) => setSections(sections.map((s, idx) => idx === i ? { ...s, [k]: v } : s));
  const removeSection = (i) => setSections(sections.filter((_, idx) => idx !== i));
  const moveSection = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= sections.length) return;
    const next = [...sections];
    [next[i], next[j]] = [next[j], next[i]];
    setSections(next);
  };
  const applyTemplate = (types) => setSections(types.map((t) => ({ type: t, text: "" })));
  const regenSection = async (tag, index) => {
    const key = `${tag}-${index}`; setRegenTag(key);
    try {
      const r = await api.post("/ai/lyrics/section", { lyrics, section: tag, index, genre, idea, language, slang, rhyme });
      setLyrics(r.data.lyrics);
      toast.success("Sección regenerada");
    } catch { toast.error("No se pudo regenerar la sección"); }
    finally { setRegenTag(null); }
  };
  const parseBlocks = (text) => {
    const blocks = []; let cur = { header: null, tag: null, lines: [] };
    (text || "").split("\n").forEach((l) => {
      const m = l.trim().match(/^\[(.+?)\]$/);
      if (m) { if (cur.header !== null || cur.lines.length) blocks.push(cur); cur = { header: l.trim(), tag: m[1].trim().toLowerCase(), lines: [] }; }
      else cur.lines.push(l);
    });
    if (cur.header !== null || cur.lines.length) blocks.push(cur);
    const counts = {};
    blocks.forEach((b) => { if (b.tag != null) { b.occ = counts[b.tag] || 0; counts[b.tag] = b.occ + 1; } });
    return blocks;
  };
  const rebuildBlocks = (blocks) => {
    const out = [];
    blocks.forEach((b) => { if (b.header) out.push(b.header); b.lines.forEach((l) => out.push(l)); });
    return out.join("\n").trim();
  };
  const lyricBlocks = useMemo(() => parseBlocks(lyrics), [lyrics]);
  const blockTotals = useMemo(() => { const t = {}; lyricBlocks.forEach((b) => { if (b.tag) t[b.tag] = (t[b.tag] || 0) + 1; }); return t; }, [lyricBlocks]);
  const blockLabel = (b) => { const cap = b.tag.charAt(0).toUpperCase() + b.tag.slice(1); return blockTotals[b.tag] > 1 ? `${cap} ${b.occ + 1}` : cap; };
  const moveBlock = (bi, dir) => { const bs = parseBlocks(lyrics); const j = bi + dir; if (j < 0 || j >= bs.length) return;[bs[bi], bs[j]] = [bs[j], bs[bi]]; setLyrics(rebuildBlocks(bs)); };
  const mergeBlock = (bi) => { const bs = parseBlocks(lyrics); if (bi + 1 >= bs.length) return; bs[bi].lines = [...bs[bi].lines, ...bs[bi + 1].lines]; bs.splice(bi + 1, 1); setLyrics(rebuildBlocks(bs)); };
  const splitBlock = (bi) => { const bs = parseBlocks(lyrics); const b = bs[bi]; if (b.lines.length < 2) return; const mid = Math.ceil(b.lines.length / 2); const second = { header: b.header, tag: b.tag, lines: b.lines.slice(mid) }; b.lines = b.lines.slice(0, mid); bs.splice(bi + 1, 0, second); setLyrics(rebuildBlocks(bs)); };
  const translateLyrics = async () => {
    if (!lyrics.trim()) return;
    setTranslating(true);
    try { const r = await api.post("/ai/lyrics/translate", { lyrics, language, genre }); setLyrics(r.data.lyrics); toast.success("Letra traducida"); }
    catch { toast.error("No se pudo traducir"); } finally { setTranslating(false); }
  };
  const rerollLyrics = async () => {
    setRerolling(true);
    try { const r = await api.post("/ai/lyrics/reroll", { lyrics, keep: "coro", genre, idea, language, slang, rhyme }); setLyrics(r.data.lyrics); toast.success("Versos regenerados (coro intacto)"); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo regenerar"); } finally { setRerolling(false); }
  };
  const annotateLangs = async () => {
    if (!lyrics.trim()) { toast.error("Escribe o genera una letra primero"); return; }
    setAnnotating(true);
    try { const r = await api.post("/ai/lyrics/annotate-langs", { lyrics, genre }); setLyrics(r.data.lyrics); toast.success("Partes marcadas por idioma"); }
    catch { toast.error("No se pudo marcar"); } finally { setAnnotating(false); }
  };
  const runVoicePreview = async () => {
    setPreviewAudio(null); setPreviewStatus("processing"); setPreviewing(true);
    try {
      const r = await api.post("/ai/voice-preview", { genre, voice: voice === "mine" ? "male" : voice, language, voice_es: voiceEs, voice_en: voiceEn });
      const id = r.data.id;
      const poll = async (n) => {
        try {
          const s = await api.get(`/ai/voice-preview/${id}`);
          if (s.data.status === "ready") { setPreviewAudio(s.data.audio_path); setPreviewStatus("ready"); setPreviewing(false); return; }
          if (s.data.status === "failed") { setPreviewStatus("failed"); setPreviewing(false); toast.error("No se pudo generar la vista previa"); return; }
        } catch { /* keep polling */ }
        if (n > 0) setTimeout(() => poll(n - 1), 4000);
        else { setPreviewing(false); setPreviewStatus("failed"); }
      };
      setTimeout(() => poll(30), 4000);
    } catch { setPreviewing(false); setPreviewStatus("failed"); toast.error("No se pudo iniciar la vista previa"); }
  };
  const composeSections = () => sections
    .filter((s) => s.text.trim())
    .map((s) => `[${s.type}]\n${s.text.trim()}`)
    .join("\n\n");

  const doGenLyrics = async () => {
    if (!idea.trim()) { toast.error("Escribe una idea primero"); return; }
    setGenLyrics(true);
    try {
      const r = await api.post("/ai/lyrics", { idea, genre, duration, language, slang, rhyme });
      setLyrics(r.data.lyrics);
      setMode("write");
      toast.success("Letra generada");
    } catch { toast.error("No se pudo generar la letra"); }
    finally { setGenLyrics(false); }
  };

  const selectVariant = async (i) => {
    try { await api.post(`/projects/${lastId}/variant/select`, { index: i }); await reload(); toast.success("Versión elegida"); }
    catch { toast.error("No se pudo elegir la versión"); }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const composedLyrics = mode === "sections" ? composeSections() : (mode === "write" ? lyrics : "");
      const r = await api.post("/songs", {
        title: title.trim() || "Mi canción",
        genre,
        voice,
        language,
        voice_es: language === "spanglish" ? voiceEs : null,
        voice_en: language === "spanglish" ? voiceEn : null,
        lyrics: composedLyrics,
        duration,
        lock_lyrics: mode !== "idea" ? lockLyrics : false,
        pronunciations: (mode !== "idea" && lockLyrics) ? prons.filter((p) => p.word.trim() && p.sounds.trim()) : [],
        idempotency_key: crypto.randomUUID(),
      });
      setLastId(r.data.id);
      await reload();
      ["title", "lyrics", "idea"].forEach((k) => clearDraft(`mfp_draft_song_${k}`));
      setTitle(""); setLyrics(""); setIdea("");
      toast.success("Generando tu canción…");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al generar");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6 fade-up">
      <div className="flex items-center gap-3">
        <Music4 className="w-6 h-6 text-[#FF334B]" />
        <h2 className="font-head text-2xl sm:text-3xl font-bold tracking-tight">Crear canción</h2>
      </div>

      <div className="mfp-card p-6 space-y-6">
        <div>
          <label className="mono-label">Título</label>
          <input data-testid="song-title-input" value={title} onChange={(e) => setTitle(e.target.value)}
            placeholder="Nombre de tu canción"
            className="mt-2 w-full bg-[#0B0C13] border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#FF334B] transition-colors" />
        </div>

        <div>
          <label className="mono-label">Género / ritmo</label>
          <div className="mt-3 flex flex-wrap gap-2">
            {GENRES.map((g) => (
              <Chip key={g} active={genre === g} onClick={() => setGenre(g)} testId={`genre-${g}`}>{g}</Chip>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4" data-testid="lyric-style-controls">
          <div>
            <label className="mono-label">Idioma</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {LANGUAGES.map((l) => (
                <Chip key={l.id} active={language === l.id} onClick={() => setLanguage(l.id)} testId={`language-${l.id}`}>{l.label}</Chip>
              ))}
            </div>
          </div>
          <div>
            <label className="mono-label">Jerga</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {SLANGS.map((s) => (
                <Chip key={s.id} active={slang === s.id} onClick={() => setSlang(s.id)} testId={`slang-${s.id}`}>{s.label}</Chip>
              ))}
            </div>
          </div>
          <div>
            <label className="mono-label">Rima</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {RHYMES.map((r) => (
                <Chip key={r.id} active={rhyme === r.id} onClick={() => setRhyme(r.id)} testId={`rhyme-${r.id}`}>{r.label}</Chip>
              ))}
            </div>
          </div>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-3">
            <button data-testid="lyrics-mode-write" onClick={() => setMode("write")}
              className={`text-sm px-3 py-1.5 rounded-lg ${mode === "write" ? "bg-white/10 text-white" : "text-[#64748B]"}`}>✍️ Escribo la letra</button>
            <button data-testid="lyrics-mode-sections" onClick={() => setMode("sections")}
              className={`text-sm px-3 py-1.5 rounded-lg ${mode === "sections" ? "bg-white/10 text-white" : "text-[#64748B]"}`}>🧱 Por secciones</button>
            <button data-testid="lyrics-mode-idea" onClick={() => setMode("idea")}
              className={`text-sm px-3 py-1.5 rounded-lg ${mode === "idea" ? "bg-white/10 text-white" : "text-[#64748B]"}`}>✨ Solo la idea</button>
          </div>

          {mode === "write" && (
            <>
              <textarea data-testid="song-lyrics-input" value={lyrics} onChange={(e) => setLyrics(e.target.value)}
                rows={7} placeholder={"[verso]\nEscribe tu letra aquí…\n[coro]\n…"}
                className={`w-full bg-[#0B0C13] border rounded-xl px-4 py-3 outline-none focus:border-[#FF334B] transition-colors font-mono text-sm leading-relaxed ${lockLyrics ? "border-[#FF9F2E]/60" : "border-white/10"}`} />
              {lyricBlocks.some((b) => b.tag) && (
                <div className="mt-2.5 space-y-1.5" data-testid="regen-sections">
                  <span className="text-[11px] text-[#64748B] flex items-center gap-1"><Sparkles className="w-3.5 h-3.5 text-[#FF9F2E]" /> Organizar y retocar bloques:</span>
                  {lyricBlocks.map((b, bi) => b.tag ? (
                    <div key={bi} className="flex items-center gap-1 bg-white/5 rounded-lg px-2 py-1" data-testid={`block-${b.tag}-${b.occ}`}>
                      <span className="text-xs font-semibold text-[#94A3B8] flex-1 truncate">{blockLabel(b)}</span>
                      <button data-testid={`regen-section-${b.tag}-${b.occ}`} title="Regenerar" onClick={() => regenSection(b.tag, b.occ)} disabled={!!regenTag}
                        className="p-1 text-[#94A3B8] hover:text-[#FF9F2E] disabled:opacity-40">{regenTag === `${b.tag}-${b.occ}` ? <Loader2 className="w-3.5 h-3.5 spin" /> : <RefreshCw className="w-3.5 h-3.5" />}</button>
                      <button data-testid={`block-up-${bi}`} title="Subir" onClick={() => moveBlock(bi, -1)} className="p-1 text-[#94A3B8] hover:text-white"><ArrowUp className="w-3.5 h-3.5" /></button>
                      <button data-testid={`block-down-${bi}`} title="Bajar" onClick={() => moveBlock(bi, 1)} className="p-1 text-[#94A3B8] hover:text-white"><ArrowDown className="w-3.5 h-3.5" /></button>
                      <button data-testid={`block-merge-${bi}`} title="Fusionar con el siguiente" onClick={() => mergeBlock(bi)} className="p-1 text-[#94A3B8] hover:text-white"><Link2 className="w-3.5 h-3.5" /></button>
                      <button data-testid={`block-split-${bi}`} title="Dividir en dos" onClick={() => splitBlock(bi)} className="p-1 text-[#94A3B8] hover:text-white"><Scissors className="w-3.5 h-3.5" /></button>
                    </div>
                  ) : null)}
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    <button data-testid="reroll-verses-button" onClick={rerollLyrics} disabled={rerolling || !lyricBlocks.some((b) => b.tag === "coro")}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border border-white/12 text-[#94A3B8] hover:border-[#FF9F2E] hover:text-[#FF9F2E] transition-colors disabled:opacity-40">
                      {rerolling ? <Loader2 className="w-3.5 h-3.5 spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Regenerar versos (mantener coro)
                    </button>
                    <button data-testid="translate-lyrics-button" onClick={translateLyrics} disabled={translating}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border border-white/12 text-[#94A3B8] hover:border-[#00F0FF] hover:text-[#00F0FF] transition-colors disabled:opacity-40">
                      {translating ? <Loader2 className="w-3.5 h-3.5 spin" /> : <Languages className="w-3.5 h-3.5" />} Traducir a {LANGUAGES.find((l) => l.id === language)?.label}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {mode === "sections" && (
            <div className="space-y-3" data-testid="sections-editor">
              <p className="text-xs text-[#64748B]">Ordena la estructura de tu canción. Cada bloque lleva su letra; la IA respeta el orden (intro, verso, coro, puente, outro).</p>
              <div className="flex flex-wrap items-center gap-2" data-testid="structure-templates">
                <span className="text-[11px] text-[#64748B]">Plantillas:</span>
                {STRUCTURE_TEMPLATES.map((t) => (
                  <button key={t.name} data-testid={`structure-template-${t.name.toLowerCase()}`} onClick={() => applyTemplate(t.types)}
                    className="text-xs font-semibold px-3 py-1.5 rounded-full border border-white/12 text-[#94A3B8] hover:border-[#FF334B] hover:text-[#FF334B] transition-colors">
                    {t.name}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-1" data-testid="structure-minimap">
                <span className="text-[11px] text-[#64748B] mr-1">Mapa:</span>
                {sections.map((s, i) => (
                  <span key={i} className="text-[10px] font-bold px-2 py-0.5 rounded bg-white/8 text-[#94A3B8]">
                    {SECTION_TYPES.find((t) => t.id === s.type)?.label || s.type}
                  </span>
                ))}
              </div>
              {sections.map((sec, i) => (
                <div key={i} className="mfp-elev rounded-xl p-3 space-y-2" data-testid={`section-block-${i}`}>
                  <div className="flex items-center gap-2">
                    <select data-testid={`section-type-${i}`} value={sec.type} onChange={(e) => updateSection(i, "type", e.target.value)}
                      className="bg-[#0B0C13] border border-white/10 rounded-lg px-2.5 py-1.5 text-sm outline-none focus:border-[#FF334B]">
                      {SECTION_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                    </select>
                    <div className="flex-1" />
                    <button data-testid={`section-up-${i}`} onClick={() => moveSection(i, -1)} disabled={i === 0} className="text-[#64748B] hover:text-white disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
                    <button data-testid={`section-down-${i}`} onClick={() => moveSection(i, 1)} disabled={i === sections.length - 1} className="text-[#64748B] hover:text-white disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
                    {sections.length > 1 && (
                      <button data-testid={`section-remove-${i}`} onClick={() => removeSection(i)} className="text-[#64748B] hover:text-red-400"><X className="w-4 h-4" /></button>
                    )}
                  </div>
                  <textarea data-testid={`section-text-${i}`} value={sec.text} onChange={(e) => updateSection(i, "text", e.target.value)} rows={3}
                    placeholder={`Letra del ${SECTION_TYPES.find((t) => t.id === sec.type)?.label.toLowerCase() || "bloque"}…`}
                    className="w-full bg-[#0B0C13] border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#FF334B] font-mono" />
                </div>
              ))}
              <button data-testid="add-section" onClick={addSection}
                className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full border border-white/12 text-[#94A3B8] hover:border-[#FF334B] hover:text-[#FF334B] transition-colors">
                <Plus className="w-4 h-4" /> Añadir sección
              </button>
            </div>
          )}

          {mode === "idea" && (
            <div className="space-y-3">
              <textarea data-testid="song-idea-input" value={idea} onChange={(e) => setIdea(e.target.value)}
                rows={4} placeholder="Describe la historia o la idea de tu canción…"
                className="w-full bg-[#0B0C13] border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#FF334B] transition-colors" />
              <button data-testid="generate-lyrics-button" onClick={doGenLyrics} disabled={genLyrics}
                className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-full border border-[#FF9F2E]/50 text-[#FF9F2E] hover:bg-[#FF9F2E]/10 transition-colors disabled:opacity-50">
                {genLyrics ? <Loader2 className="w-4 h-4 spin" /> : <Wand2 className="w-4 h-4" />}
                Generar letra con IA
              </button>
              {lyrics && (
                <textarea value={lyrics} onChange={(e) => setLyrics(e.target.value)} rows={6}
                  className="w-full bg-[#0B0C13] border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#FF334B] font-mono text-sm" />
              )}
            </div>
          )}

          {mode !== "idea" && (
            <>
              <button type="button" data-testid="toggle-lock-lyrics" onClick={() => setLockLyrics(!lockLyrics)}
                className={`mt-3 w-full text-left p-4 rounded-xl border transition-colors ${lockLyrics ? "border-[#FF9F2E] bg-[#FF9F2E]/10" : "border-white/12 hover:border-white/25"}`}>
                <div className="flex items-center justify-between">
                  <span className="font-head font-semibold text-sm flex items-center gap-2"><Lock className="w-4 h-4 text-[#FF9F2E]" /> Letra bloqueada (exacta)</span>
                  <span className={`w-9 h-5 rounded-full flex items-center px-0.5 transition-colors ${lockLyrics ? "bg-[#FF9F2E] justify-end" : "bg-white/15"}`}>
                    <span className="w-4 h-4 rounded-full bg-white" />
                  </span>
                </div>
                <p className="text-xs text-[#64748B] mt-1">La IA no reescribe tu letra: se envía tal cual, palabra por palabra, al modelo de música.</p>
              </button>

              {lockLyrics && (
                <div className="mt-3 mfp-elev rounded-xl p-4 space-y-3" data-testid="pronunciation-dict">
                  <div className="flex items-center justify-between">
                    <span className="mono-label">Diccionario de pronunciación</span>
                    <button data-testid="add-pronunciation" onClick={addPron}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#FF9F2E] hover:opacity-80">
                      <Plus className="w-3.5 h-3.5" /> Palabra
                    </button>
                  </div>
                  <p className="text-[11px] text-[#64748B]">Escribe cómo suena una palabra difícil (ej. "WhatsApp" → "guatsap") para que la IA la cante bien.</p>
                  {prons.length === 0 && <p className="text-xs text-[#475569]">Sin palabras aún. Añade las que quieras forzar.</p>}
                  {prons.map((p, i) => (
                    <div key={i} className="flex items-center gap-2" data-testid={`pronunciation-row-${i}`}>
                      <input data-testid={`pron-word-${i}`} value={p.word} onChange={(e) => updatePron(i, "word", e.target.value)} placeholder="Palabra"
                        className="flex-1 bg-[#0B0C13] border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#FF334B]" />
                      <span className="text-[#64748B]">→</span>
                      <input data-testid={`pron-sounds-${i}`} value={p.sounds} onChange={(e) => updatePron(i, "sounds", e.target.value)} placeholder="Cómo suena"
                        className="flex-1 bg-[#0B0C13] border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#FF334B]" />
                      <button data-testid={`remove-pronunciation-${i}`} onClick={() => removePron(i)} className="text-[#64748B] hover:text-red-400">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div>
          <label className="mono-label">Voz que canta</label>
          <div className="mt-3 flex flex-wrap gap-2">
            {VOICES.map((v) => (
              <Chip key={v.id} active={voice === v.id} onClick={() => setVoice(v.id)} testId={`voice-${v.id}`}>{v.label}</Chip>
            ))}
          </div>
          {voice === "mine" && <VoiceUploader />}
          {language === "spanglish" && (
            <div className="mt-3 grid sm:grid-cols-2 gap-3" data-testid="dual-voice-controls">
              <div>
                <span className="text-[11px] text-[#64748B]">Voz para partes en español</span>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {VOICES.filter((v) => v.id !== "mine").map((v) => (
                    <Chip key={v.id} active={voiceEs === v.id} onClick={() => setVoiceEs(v.id)} testId={`voice-es-${v.id}`}>{v.label}</Chip>
                  ))}
                </div>
              </div>
              <div>
                <span className="text-[11px] text-[#64748B]">Voz para partes en inglés</span>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {VOICES.filter((v) => v.id !== "mine").map((v) => (
                    <Chip key={v.id} active={voiceEn === v.id} onClick={() => setVoiceEn(v.id)} testId={`voice-en-${v.id}`}>{v.label}</Chip>
                  ))}
                </div>
              </div>
              <button data-testid="annotate-langs-button" onClick={annotateLangs} disabled={annotating}
                className="sm:col-span-2 inline-flex items-center justify-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-white/12 text-[#94A3B8] hover:border-[#FF334B] hover:text-[#FF334B] transition-colors disabled:opacity-40">
                {annotating ? <Loader2 className="w-3.5 h-3.5 spin" /> : <Languages className="w-3.5 h-3.5" />} Marcar partes por idioma (ES/EN)
              </button>
            </div>
          )}
          <div className="mt-3" data-testid="voice-preview">
            <div className="flex items-center gap-2 flex-wrap">
              <button data-testid="voice-preview-button" onClick={runVoicePreview} disabled={previewing}
                className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full border border-[#FF9F2E]/50 text-[#FF9F2E] hover:bg-[#FF9F2E]/10 transition-colors disabled:opacity-50">
                {previewing ? <Loader2 className="w-4 h-4 spin" /> : <Headphones className="w-4 h-4" />}
                {previewing ? "Generando muestra…" : "Vista previa de voz (~10s)"}
              </button>
              <span className="text-[11px] text-[#64748B]">Consume un poco de saldo Fal</span>
            </div>
            {previewStatus === "ready" && previewAudio && (
              <audio data-testid="voice-preview-audio" src={mediaUrl(previewAudio)} controls className="mt-2 w-full max-w-sm" />
            )}
          </div>
        </div>

        <div>
          <label className="mono-label">Duración · {duration}s</label>
          <input data-testid="song-duration-slider" type="range" min="20" max="240" step="10" value={duration}
            onChange={(e) => setDuration(Number(e.target.value))}
            className="mt-3 w-full accent-[#FF334B]" />
        </div>

        <div className="flex items-center gap-2 rounded-xl border border-white/8 bg-[#0B0C13] px-4 py-3" data-testid="song-cost-estimate">
          <Coins className="w-4 h-4 text-[#FF9F2E] shrink-0" />
          <span className="text-sm text-[#94A3B8]">
            Costo: <strong className="text-white">{fmtCredits(songCredits())}</strong>
            <span className="text-[#64748B]"> · {voice === "mine" ? "1 versión" : "2 versiones"} · {duration}s</span>
          </span>
        </div>

        <button data-testid="generate-song-button" onClick={submit} disabled={submitting}
          className="w-full inline-flex items-center justify-center gap-2 bg-[#FF334B] hover:bg-[#E0223A] text-white font-semibold py-3.5 rounded-full transition-colors disabled:opacity-60">
          {submitting ? <Loader2 className="w-5 h-5 spin" /> : <Sparkles className="w-5 h-5" />}
          Generar canción
        </button>
      </div>

      {current && (
        <div className="mfp-card p-6 space-y-4 fade-up" data-testid="song-result">
          <div className="flex items-center justify-between">
            <div className="font-head font-semibold text-lg">{current.title}</div>
            <StatusBadge status={current.status} />
          </div>
          {current.status === "processing" && (
            <div className="flex items-center gap-3 text-[#94A3B8]">
              <Loader2 className="w-5 h-5 spin text-[#FF9F2E]" />
              Componiendo y cantando tu canción… (puede tardar ~15-40s)
            </div>
          )}
          {current.status === "queued" && (
            <div className="flex items-center gap-3 text-[#94A3B8]"><Loader2 className="w-5 h-5 spin text-sky-400" /> En cola, empezará en breve…</div>
          )}
          {current.status === "failed" && <p className="text-red-400 text-sm">{current.error}</p>}
          {current.status === "ready" && current.result?.variants?.length > 1 ? (
            <div className="space-y-3" data-testid="song-variants">
              <p className="text-sm text-[#94A3B8]">Generamos 2 versiones — escucha y elige tu favorita:</p>
              {current.result.variants.map((v, i) => {
                const chosen = current.result.audio_path === v.audio_path;
                return (
                  <div key={i} className={`rounded-2xl p-3 border ${chosen ? "border-[#FF334B] bg-[#FF334B]/5" : "border-white/10"}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-head font-semibold text-sm">{v.label || `Versión ${i + 1}`}</span>
                      <button data-testid={`select-variant-${i}`} onClick={() => selectVariant(i)}
                        className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${chosen ? "bg-[#FF334B] text-white" : "border border-white/12 hover:border-[#FF334B] hover:text-[#FF334B]"}`}>
                        {chosen ? "✓ Elegida" : "Elegir esta"}
                      </button>
                    </div>
                    <WaveformPlayer src={mediaUrl(v.audio_path)} downloadName={`${current.title}-v${i + 1}.wav`} />
                  </div>
                );
              })}
              {current.result.lyrics && (
                <pre className="whitespace-pre-wrap text-sm text-[#94A3B8] font-mono bg-[#0B0C13] rounded-xl p-4 border border-white/8">{current.result.lyrics}</pre>
              )}
            </div>
          ) : current.status === "ready" && current.result?.audio_path && (
            <>
              <WaveformPlayer src={mediaUrl(current.result.audio_path)} downloadName={`${current.title}.wav`} />
              {current.result.lyrics && (
                <pre className="whitespace-pre-wrap text-sm text-[#94A3B8] font-mono bg-[#0B0C13] rounded-xl p-4 border border-white/8">{current.result.lyrics}</pre>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
